<template>
  <TransitionRoot as="template" :show="sidebarOpen">
    <Dialog as="div" class="relative z-50 xl:hidden" @close="$emit('update:sidebarOpen', false)">
      <div class="fixed inset-0 flex">
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button type="button" class="-m-2.5 p-2.5" @click="$emit('update:sidebarOpen', false)">
                  <span class="sr-only">{{ t("msg.closeSidebar") }}</span>
                  <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>

            <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-background px-6 ring-1 ring-white/10">
              <div class="flex h-16 shrink-0 items-center">
                <img
                  class="h-13 w-auto pt-4"
                  src="/assets/images/Knocknoc_long_darkbg_Large_redish.png"
                  alt="Knocknoc logo"
                />
              </div>
              <nav class="flex flex-1 flex-col">
                <ul role="list" class="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" class="-mx-2 space-y-1">
                      <li v-for="item in navigation" :key="item.name">
                        <router-link
                          :to="item.route"
                          :class="[
                            route.fullPath == item.route
                              ? 'bg-primary text-white'
                              : 'text-[#ccd1c4] hover:bg-primary/30 hover:text-white',
                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                          ]"
                        >
                          <component :is="item.icon" class="h-6 w-6 shrink-0" aria-hidden="true" />
                          {{ item.name }}
                        </router-link>
                      </li>
                    </ul>
                  </li>
                  <li class="-mx-6 mt-auto" @click="logout">
                    <a
                      href="#"
                      class="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white hover:bg-primary/30"
                    >
                      <ArrowLeftOnRectangleIcon class="h-6 w-6 shrink-0" />
                      <span>{{ t("msg.logout") }}</span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <div class="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col">
    <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-black/10 px-6 ring-1 ring-white/5">
      <div class="flex h-16 shrink-0 items-center">
        <img class="h-13 w-auto pt-4" src="/assets/images/Knocknoc_long_darkbg_Large_redish.png" alt="Knocknoc logo" />
      </div>
      <nav class="flex flex-1 flex-col">
        <ul role="list" class="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" class="-mx-2 space-y-1">
              <li v-for="item in navigation" :key="item.name">
                <router-link
                  :to="item.route"
                  :class="[
                    route.fullPath == item.route
                      ? 'bg-primary text-white'
                      : 'text-[#ccd1c4] hover:bg-primary/30 hover:text-white',
                    'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                  ]"
                >
                  <component :is="item.icon" class="h-6 w-6 shrink-0" aria-hidden="true" />
                  {{ item.name }}
                </router-link>
              </li>
            </ul>
          </li>
          <li class="-mx-6 mt-auto" @click="logout">
            <a
              href="#"
              class="flex items-center gap-x-2 px-6 py-3 text-sm font-semibold leading-6 text-white hover:bg-primary/30"
            >
              <ArrowLeftOnRectangleIcon class="h-6 w-6 shrink-0" />
              <span>{{ t("msg.logout") }}</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import {
  ArrowLeftOnRectangleIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  HomeIcon,
  KeyIcon,
  ServerIcon,
  UserGroupIcon,
  UserPlusIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useAdminStore } from "../pinia/admin.ts";

const { t } = useI18n();

const adminStore = useAdminStore();

const route = useRoute();
const router = useRouter();

defineProps({
  sidebarOpen: {
    required: true,
  },
});

defineEmits(["update:sidebarOpen"]);

const navigationItems = [
  { name: t("msg.home"), route: "/admin/home", icon: HomeIcon },
  { name: t("msg.agents"), route: "/admin/agents", icon: CommandLineIcon },
  { name: t("msg.knocs"), route: "/admin/knocs", icon: ServerIcon },
  { name: t("msg.groups"), route: "/admin/groups", icon: UserGroupIcon },
  { name: t("msg.users"), route: "/admin/users", icon: UsersIcon },
  { name: t("msg.admins"), route: "/admin/admins", icon: UserPlusIcon },
  { name: t("msg.logs"), route: "/admin/logs", icon: BookOpenIcon },
  { name: t("msg.apiKeys"), route: "/admin/api-keys", icon: KeyIcon },
  { name: t("msg.settings"), route: "/admin/settings", icon: Cog6ToothIcon },
];

const navigation = computed(() => {
  return navigationItems.filter((item) => (item.filter ? item.filter(adminStore.user) : true));
});

const logout = async () => {
  await adminStore.logout();
  router.push({ name: "AdminLogin" });
};
</script>
